import { LazyLoader } from "@NextSkyTheme/lazy-load";

function safeParseStorage(key, fallback = []) {
  try {
    const value = localStorage.getItem(key);
    if (!value) return fallback;
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    console.warn(`Unable to read ${key} from localStorage.`, error);
    safeRemoveStorage(key);
    return fallback;
  }
}

function safeRemoveStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Unable to remove ${key} from localStorage.`, error);
  }
}

function normalizeProductId(value) {
  const id = String(value || "").trim();
  return /^\d+$/.test(id) ? id : "";
}

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.abortController = null;
    this.hasLoaded = false;
    this.isLoading = false;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (this.hasLoaded || this.isLoading || this.observer) return;

    if (typeof IntersectionObserver === "undefined") {
      this.initData();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.disconnectObserver();
        this.initData();
      },
      {
        rootMargin: "0px 0px 100px 0px",
      }
    );
    this.observer.observe(this);
  }

  initData() {
    const savedProductsArr = safeParseStorage("recently-viewed-products", []);
    this.getStoredProducts(savedProductsArr);
  }

  getStoredProducts(arr) {
    const limit = Number.parseInt(this.dataset?.limit, 10);
    let productAjaxURL = "";
    if (Number.isFinite(limit) && limit > 0) {
      const idsToUse = arr
        .map(normalizeProductId)
        .filter(Boolean)
        .slice(0, limit);
      if (idsToUse.length > 0) {
        const query = idsToUse.join("%20OR%20id:");
        productAjaxURL = `&q=id:${query}`;
      }
    }
    if (!productAjaxURL) return;
    this.abortFetch();
    this.isLoading = true;
    this.abortController = new AbortController();
    const controller = this.abortController;
    fetch(`${this.dataset.url}${productAjaxURL}`, { signal: controller.signal })
      .then((response) => response.text())
      .then((text) => {
        if (!this.isConnected || this.abortController !== controller) return;
        const html = document.createElement("div");
        html.innerHTML = text;
        const recentlyViewedProducts = html.querySelector(
          "recently-viewed-products"
        );
        if (
          recentlyViewedProducts &&
          recentlyViewedProducts.innerHTML.trim().length
        ) {
          this.replaceChildren(...recentlyViewedProducts.childNodes);
        }
        this.hasLoaded = true;
        new LazyLoader(".image-lazy-load");
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        console.error(e);
      })
      .finally(() => {
        if (this.abortController === controller) {
          this.abortController = null;
          this.isLoading = false;
        }
      });
  }

  disconnectedCallback() {
    this.disconnectObserver();
    this.abortFetch();
  }

  disconnectObserver() {
    if (!this.observer) return;
    this.observer.unobserve(this);
    this.observer.disconnect();
    this.observer = null;
  }

  abortFetch() {
    if (!this.abortController) return;
    this.abortController.abort();
    this.abortController = null;
    this.isLoading = false;
  }
}
if (!customElements.get("recently-viewed-products")) {
  customElements.define("recently-viewed-products", RecentlyViewedProducts);
}
